import { useNotifications } from '@armhr/ui';
import {
  CheckCircleOutline,
  Close,
  EmailOutlined,
  VisibilityOff,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { useMemo, useRef, useState } from 'react';

import { useApi } from '../../hooks/useApi';
import { useApiData } from '../../hooks/useApiData';
import { useUser } from '../../hooks/useUser';

const AdminHandbookAssignModal: React.FC<{
  open: boolean;
  handleClose: () => void;
  handleUpdateAssign: (assignments: {
    [handbook_id: number]: EmployeeHandbookAssignment[];
  }) => void;
  assignments: { [handbook_id: number]: EmployeeHandbookAssignment[] };
  handbookId: number;
  handbookName?: string;
}> = ({
  open,
  handleClose,
  handleUpdateAssign,
  assignments,
  handbookId,
  handbookName,
}) => {
  const api = useApi();
  const { entity } = useUser();
  const { showNotification } = useNotifications();
  const firstRender = useRef(true);
  const [loading, setLoading] = useState<boolean>(false);
  const existingDueDateStr =
    assignments[handbookId]?.find((a) => a.due_date)?.due_date ?? null;
  const [dueDate, setDueDate] = useState<Date | null>(
    existingDueDateStr ? new Date(existingDueDateStr) : null
  );
  const [readOnly, setReadOnly] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<string[]>(
    assignments[handbookId].map((a) => a.employee_id)
  );
  const hasAssignments = (assignments[handbookId]?.length ?? 0) > 0;
  const [hideSigned, setHideSigned] = useState(hasAssignments);
  const [hideAssigned, setHideAssigned] = useState(hasAssignments);

  const { data: employees, loading: employeesLoading } = useApiData((api) =>
    api.company.getEmployees()
  );

  const currentAssignedIds = useMemo(
    () => new Set(assignments[handbookId].map((a) => a.employee_id)),
    [assignments, handbookId]
  );

  const assignmentStatusMap = useMemo(() => {
    const map = new Map<string, EmployeeHandbookAssignment['status']>();
    assignments[handbookId]?.forEach((a) => {
      map.set(a.employee_id, a.status);
    });
    return map;
  }, [assignments, handbookId]);

  const signedCount = useMemo(
    () =>
      assignments[handbookId]?.filter((a) => a.status === 'signed').length ?? 0,
    [assignments, handbookId]
  );

  const assignedCount = useMemo(
    () =>
      assignments[handbookId]?.filter(
        (a) =>
          a.status === 'pending' ||
          a.status === 'read_only' ||
          a.status === 'overdue'
      ).length ?? 0,
    [assignments, handbookId]
  );

  const selectionSummary = useMemo(() => {
    const total = selectedIds.length;
    const existing = selectedIds.filter((id) => currentAssignedIds.has(id));
    const newlyAdded = total - existing.length;
    const toRemove = [...currentAssignedIds].filter(
      (id) => !selectedIds.includes(id)
    ).length;
    return { total, newlyAdded, toRemove };
  }, [selectedIds, currentAssignedIds]);

  const hasChanges =
    selectionSummary.newlyAdded > 0 || selectionSummary.toRemove > 0;
  const needsDueDate =
    selectionSummary.newlyAdded > 0 && !readOnly && dueDate === null;
  const canSave = hasChanges && !needsDueDate;

  const sortedEmployees = useMemo(() => {
    if (!employees) return [];
    const statusOrder = (id: string) => {
      const status = assignmentStatusMap.get(id);
      if (status === 'signed') return 0;
      if (
        status === 'pending' ||
        status === 'read_only' ||
        status === 'overdue'
      )
        return 1;
      return 2;
    };
    return [...employees].sort((a, b) => {
      const orderDiff = statusOrder(a.id) - statusOrder(b.id);
      if (orderDiff !== 0) return orderDiff;
      return (a.last_name ?? '').localeCompare(b.last_name ?? '');
    });
  }, [employees, assignmentStatusMap]);

  const filteredEmployees = useMemo(() => {
    if (!hideSigned && !hideAssigned) return sortedEmployees;
    return sortedEmployees.filter((emp) => {
      const status = assignmentStatusMap.get(emp.id);
      if (hideSigned && status === 'signed') return false;
      if (
        hideAssigned &&
        (status === 'pending' || status === 'read_only' || status === 'overdue')
      )
        return false;
      return true;
    });
  }, [sortedEmployees, assignmentStatusMap, hideSigned, hideAssigned]);

  const hiddenIds = useMemo(() => {
    if (!hideSigned && !hideAssigned) return new Set<string>();
    const visibleIds = new Set(filteredEmployees.map((e) => e.id));
    return new Set(
      sortedEmployees.filter((e) => !visibleIds.has(e.id)).map((e) => e.id)
    );
  }, [sortedEmployees, filteredEmployees, hideSigned, hideAssigned]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'first_name',
        headerName: 'First name',
        flex: 1,
        minWidth: 120,
      },
      {
        field: 'middle_initial',
        headerName: 'MI',
        width: 60,
        sortable: false,
      },
      {
        field: 'last_name',
        headerName: 'Last name',
        flex: 1,
        minWidth: 120,
      },
      {
        field: 'assignment_status',
        headerName: 'Status',
        width: 130,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        valueGetter: (params) => {
          return assignmentStatusMap.get(params.row.id) ?? null;
        },
        renderCell: (params) => {
          const status = params.value as string | null;
          if (!status) return null;
          if (status === 'signed')
            return (
              <Chip
                icon={<CheckCircleOutline sx={{ fontSize: 14 }} />}
                label="Signed"
                size="small"
                color="success"
                variant="outlined"
                sx={{ height: 24 }}
              />
            );
          if (status === 'overdue')
            return (
              <Chip
                label="Overdue"
                size="small"
                color="error"
                variant="outlined"
                sx={{ height: 24 }}
              />
            );
          // pending and read_only both just show as "Assigned" in this context
          return (
            <Chip
              label="Assigned"
              size="small"
              variant="outlined"
              sx={{ height: 24 }}
            />
          );
        },
      },
    ],
    [assignmentStatusMap]
  );

  const handleAssign = async () => {
    setLoading(true);

    const newlyAddedIds = selectedIds.filter(
      (id) => !currentAssignedIds.has(id)
    );
    const removedIds = [...currentAssignedIds].filter(
      (id) => !selectedIds.includes(id)
    );

    try {
      // Unassign removed employees
      if (removedIds.length > 0) {
        const unassignPayload = assignments[handbookId]
          .filter((a) => removedIds.includes(a.employee_id))
          .map((a) => Number(a.id));
        await api.company.unassignEmployeeHandbook(unassignPayload);
      }

      // Assign only NEW employees (preserves existing statuses, avoids re-sending emails)
      if (newlyAddedIds.length > 0) {
        const assignPayload = newlyAddedIds.map((eId) => ({
          employee_id: eId,
          client_id: entity?.id,
          handbook_id: handbookId,
          status: readOnly ? 'read_only' : 'pending',
          due_date: dueDate ? dueDate.toISOString() : null,
        })) as EmployeeHandbookAssignment[];
        await api.company.assignEmployeeHandbook(assignPayload);
      }

      // Refresh from server to get accurate state
      const { results: freshAssignments } =
        await api.company.getEmployeeAssignments(handbookId);
      handleUpdateAssign({ ...assignments, [handbookId]: freshAssignments });

      showNotification({
        message: 'Successfully saved handbook assignments.',
        severity: 'success',
        autoHideDuration: 4000,
      });
    } catch (e) {
      showNotification({
        message: 'Unable to save handbook assignments.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h5" component="span" fontWeight={600}>
            Assign Handbook
          </Typography>
          {handbookName && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {handbookName}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={() => {
            setSelectedIds(assignments[handbookId].map((a) => a.employee_id));
            handleClose();
          }}
          size="small"
          aria-label="Close"
          sx={{ mt: 0.5 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', pb: 0 }}>
        {/* Assignment settings */}
        <Paper
          variant="outlined"
          sx={{ px: 2.5, py: 2, mb: 2, borderRadius: 2 }}
        >
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{
              mb: 1.5,
              textTransform: 'uppercase',
              fontSize: '0.7rem',
              letterSpacing: 0.5,
            }}
          >
            Assignment settings
          </Typography>
          <Stack direction="row" spacing={3} alignItems="flex-start">
            <DatePicker
              label="Due date"
              value={dueDate}
              onChange={(val: Date | null) => setDueDate(val)}
              disabled={selectionSummary.newlyAdded === 0 || readOnly}
              slotProps={{
                textField: {
                  size: 'small',
                  error: needsDueDate,
                  helperText: needsDueDate
                    ? 'Required for new assignments'
                    : ' ',
                  sx: { minWidth: 200 },
                },
              }}
            />
            <Divider orientation="vertical" flexItem />
            <Box sx={{ pt: 0.25 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Switch
                  checked={readOnly}
                  onChange={(e) => {
                    setReadOnly(e.target.checked);
                    if (e.target.checked) setDueDate(null);
                  }}
                  size="small"
                />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    View only
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    No signature or deadline required
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </Paper>

        {/* Employee grid */}
        <Box sx={{ flex: 1, minHeight: 350 }}>
          <DataGrid
            loading={employeesLoading}
            columns={columns}
            rows={filteredEmployees}
            checkboxSelection
            disableRowSelectionOnClick
            slots={{ toolbar: QuickSearchToolbar }}
            slotProps={{
              toolbar: {
                hideSigned,
                hideAssigned,
                signedCount,
                assignedCount,
                onToggleSigned: () => setHideSigned((v) => !v),
                onToggleAssigned: () => setHideAssigned((v) => !v),
              } as any,
            }}
            rowSelectionModel={selectedIds}
            onRowSelectionModelChange={(selection) => {
              if (firstRender.current) {
                firstRender.current = false;
                return;
              }
              // Preserve selections for rows hidden by the filters
              const hiddenSelections = selectedIds.filter((id) =>
                hiddenIds.has(id)
              );
              const merged = [
                ...new Set([...(selection as string[]), ...hiddenSelections]),
              ];
              setSelectedIds(merged);
            }}
            initialState={{
              pagination: { paginationModel: { pageSize: 15 } },
            }}
            pageSizeOptions={[15, 50, 100]}
            density="compact"
            sx={{
              height: 580,
              background: 'white',
              fontSize: '0.875rem',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              '& .MuiDataGrid-withBorderColor': {
                borderColor: 'divider',
              },
              '& .MuiDataGrid-row': {
                cursor: 'default',
              },
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: 'grey.50',
              },
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-footerContainer': {
                height: 42,
                minHeight: 42,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
              },
              '& .MuiDataGrid-columnHeader': {
                bgcolor: 'grey.50',
                color: '#000',
              },
              '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:active':
                {
                  outline: 'none',
                },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          flexDirection: 'column',
          alignItems: 'stretch',
          px: 3,
          pt: 1.5,
          pb: 2,
          gap: 1.5,
        }}
      >
        {/* Selection summary + email notice */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={`${selectionSummary.total} selected`}
              size="small"
              color={selectionSummary.total > 0 ? 'primary' : 'default'}
              variant="outlined"
            />
            {selectionSummary.newlyAdded > 0 && (
              <Typography variant="caption" color="success.main">
                +{selectionSummary.newlyAdded} new assignments
              </Typography>
            )}
            {selectionSummary.toRemove > 0 && (
              <Typography variant="caption" color="error.main">
                -{selectionSummary.toRemove} removed assignments
              </Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <EmailOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              Newly assigned employees will be notified via email
            </Typography>
          </Stack>
        </Stack>

        {/* Validation hint */}
        {needsDueDate && (
          <Alert severity="warning" sx={{ py: 0, fontSize: '0.8rem' }}>
            Set a due date or enable &quot;View only&quot; before saving.
          </Alert>
        )}

        {/* Actions */}
        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={() => {
              setSelectedIds(assignments[handbookId].map((a) => a.employee_id));
              handleClose();
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            disabled={!canSave}
            loading={loading}
            variant="contained"
            onClick={handleAssign}
          >
            Save assignments
          </LoadingButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

function QuickSearchToolbar(props: {
  hideSigned: boolean;
  hideAssigned: boolean;
  signedCount: number;
  assignedCount: number;
  onToggleSigned: () => void;
  onToggleAssigned: () => void;
}) {
  const {
    hideSigned,
    hideAssigned,
    signedCount,
    assignedCount,
    onToggleSigned,
    onToggleAssigned,
  } = props;
  return (
    <GridToolbarContainer sx={{ pb: 1, gap: 1 }}>
      <Chip
        icon={hideSigned ? <VisibilityOff sx={{ fontSize: 14 }} /> : undefined}
        label={
          hideSigned
            ? `Show Signed (${signedCount})`
            : `Hide Signed (${signedCount})`
        }
        size="small"
        variant={hideSigned ? 'filled' : 'outlined'}
        color={hideSigned ? 'success' : 'default'}
        onClick={onToggleSigned}
        sx={{ cursor: 'pointer' }}
      />
      <Chip
        icon={
          hideAssigned ? <VisibilityOff sx={{ fontSize: 14 }} /> : undefined
        }
        label={
          hideAssigned
            ? `Show Assigned (${assignedCount})`
            : `Hide Assigned (${assignedCount})`
        }
        size="small"
        variant={hideAssigned ? 'filled' : 'outlined'}
        color={hideAssigned ? 'primary' : 'default'}
        onClick={onToggleAssigned}
        sx={{ cursor: 'pointer' }}
      />
      <GridToolbarQuickFilter
        debounceMs={500}
        sx={{ marginLeft: 'auto' }}
        placeholder="Search employees..."
      />
    </GridToolbarContainer>
  );
}

export default AdminHandbookAssignModal;
